import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from './TanTable';
import { useHistoryPanelData } from '../hooks/useHistoryPanelData';
import { formatHistoryDateTime, formatIsoDateOnly } from '../utils/dateUtils';

interface AdjustmentRecord {
  recno: number;
  adjustdatetime: string | null;
  adjtype: string;
  mrnum: string;
  masterdelta: number;
  masteruom: string;
  secondarydelta: number;
  secondaryuom: string;
  olddate: string | null;
  newdate: string | null;
  adjreason: string;
  adjcomments: string;
}

interface AdjustmentsResponse {
  count: number;
  records: AdjustmentRecord[];
}

interface AdjustmentsPanelProps {
  pronumber: number | null;
  /** Defer fetching until the panel is actually shown. */
  active?: boolean;
  /** Bump this number to trigger a refetch from the parent. */
  refreshNonce?: number;
}

export const AdjustmentsPanel: React.FC<AdjustmentsPanelProps> = ({
  pronumber,
  active = true,
  refreshNonce = 0,
}) => {
  const { data, loading, error } = useHistoryPanelData<AdjustmentsResponse>(
    pronumber ? `/orders/${pronumber}/adjustments` : null,
    active,
    refreshNonce,
    'Failed to fetch adjustments'
  );

  const columns = useMemo<ColumnDef<AdjustmentRecord>[]>(
    () => [
      {
        accessorKey: 'adjtype',
        header: 'Type',
        size: 120,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'adjustdatetime',
        header: 'Date/Time',
        size: 160,
        cell: (info) => formatHistoryDateTime(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'mrnum',
        header: 'MR#',
        size: 100,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'masterdelta',
        header: 'Master',
        size: 80,
        // V1 rule: a zero delta renders blank (and blanks its UoM).
        cell: (info) => {
          const v = info.getValue() as number | null;
          return v ? String(v) : '-';
        },
      },
      {
        accessorKey: 'masteruom',
        header: 'UoM',
        size: 90,
        cell: (info) => {
          if (!info.row.original.masterdelta) return '-';
          return (info.getValue() as string) || '-';
        },
      },
      {
        accessorKey: 'secondarydelta',
        header: 'Secondary',
        size: 90,
        cell: (info) => {
          const v = info.getValue() as number | null;
          return v ? String(v) : '-';
        },
      },
      {
        accessorKey: 'secondaryuom',
        header: 'UoM',
        size: 90,
        cell: (info) => {
          if (!info.row.original.secondarydelta) return '-';
          return (info.getValue() as string) || '-';
        },
      },
      {
        accessorKey: 'olddate',
        header: 'Old Date',
        size: 100,
        cell: (info) => formatIsoDateOnly(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'newdate',
        header: 'New Date',
        size: 100,
        cell: (info) => formatIsoDateOnly(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'adjreason',
        header: 'Reason',
        size: 180,
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '-';
          return (
            <div className="truncate" title={v}>
              {v}
            </div>
          );
        },
      },
      {
        accessorKey: 'adjcomments',
        header: 'Comments',
        size: 220,
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '-';
          return (
            <div className="truncate" title={v}>
              {v}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}
      <div className="flex-1 min-h-[200px]">
        <TanTable
          data={data?.records || []}
          columns={columns}
          loading={loading}
          cardTitle="Adjustments"
          searchPlaceholder="Search adjustments..."
          settingsKey="pro-history-adjustments"
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          enableZebraStriping={false}
          defaultSorting={[{ id: 'adjustdatetime', desc: true }]}
        />
      </div>
    </div>
  );
};

export default AdjustmentsPanel;
